import { releaseService } from "@/app/providers/services";
import { useNotify } from "@/common/notifications";
import { setProject, setRelease, setScope, useScope } from "@/redux/scope";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { useConfirmDialog } from "@imminently/decisively-ui/ConfirmDialog";
import type { Release } from "@packages/commons";
import { push } from "connected-react-router";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { getReleaseUiName } from "../utils";

/**
 * Returns an async function that fetches a release by ID and atomically sets
 * workspace + project + release in scope from the release data.
 * Useful for navigating to any resource that has an associated release ID
 * (e.g. a session) without needing to know the workspace or project upfront.
 */
export const useLoadScopeByRelease = () => {
  const dispatch = useDispatch();
  const scope = useScope();

  return useCallback(
    async (releaseId: string) => {
      const release = await releaseService.getOne(releaseId, { graph: false, $fields: ["id", "workspace", "model"], fast: true }) as Release;
      dispatch(setScope({
        ...scope,
        workspace: release.workspace,
        project: release.model as string,
        release: release.id,
      }));
    },
    [dispatch, scope],
  );
};

export const useViewRelease = () => {
  const dispatch = useDispatch();
  return useCallback(
    (releaseId: string, projectId?: string) => {
      // due to our saga, we actually want to set release first
      dispatch(setRelease(releaseId));
      // then, if they provide a project we switch and navigate
      if (projectId) {
        dispatch(setProject(projectId));
        // only want to go to build page after changing project
        // otherwise, keep whatever page they were on
        dispatch(push("/build"));
      }
    },
    [dispatch],
  );
};

export const useEditRelease = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { mutateAsync, ...query } = releaseService.useUpdate();

  const editRelease = useCallback(
    async (releaseId: string, data: { description?: string; locale: string }) => {
      try {
        const res = await mutateAsync({
          id: releaseId,
          payload: data,
        });

        if (res?.id) {
          releaseService.invalidateAllQueries();
          dispatch(setRelease(res.id));
          notify.info("Release updated");
          return res;
        }
      } catch {
        notify.error("Failed to update release");
        throw new Error("Failed to update release");
      }
    },
    [dispatch, notify],
  );

  return { editRelease, ...query };
};

export const useDeleteRelease = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { release: scopeReleaseId } = useScope();
  const { mutateAsync, ...query } = releaseService.useDelete();
  const { pushConfirmDialog } = useConfirmDialog();

  const deleteRelease = useCallback(
    (release: { id: string; releaseNo?: number }) => {
      const name = getReleaseUiName(release);
      pushConfirmDialog({
        layer: WORKSPACE_LAYER,
        title: "Delete Release",
        description: `Are you sure you want to delete ${name}? This action cannot be undone.`,
        confirmLabel: "Yes, delete",
        onConfirm: async () => {
          try {
            await mutateAsync(release.id);
            releaseService.invalidateAllQueries();
            notify.info("Release deleted");
            if (scopeReleaseId === release.id) {
              dispatch(setRelease(null));
            }
          } catch {
            notify.error("Failed to delete release");
          }
        },
      });
    },
    [dispatch, mutateAsync, notify, pushConfirmDialog, scopeReleaseId],
  );

  return { deleteRelease, ...query };
};

export const useCloneRelease = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { mutateAsync, ...query } = releaseService.useUpdate();

  const cloneRelease = useCallback(
    async (baseReleaseId: string, payload: { description?: string; locale: string; canVector?: boolean }) => {
      try {
        const res = await mutateAsync({
          id: "", // path will override
          path: `releases/${baseReleaseId}/custom/clone`,
          payload,
        });

        if (res?.id) {
          releaseService.invalidateAllQueries();
          dispatch(setRelease(res.id));
          notify.info("Release duplicated");
          return res;
        }
      } catch {
        notify.error("Failed to duplicate release");
        throw new Error("Failed to duplicate release");
      }
    },
    [dispatch, notify],
  );

  return { cloneRelease, ...query };
};

export const useActivateRelease = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { mutateAsync, ...query } = releaseService.useUpdate();

  const activateRelease = useCallback(
    async (releaseId: string) => {
      try {
        const res = await mutateAsync({
          id: "",
          path: `releases/${releaseId}/custom/activate`,
          payload: {},
        });

        if (res?.id) {
          releaseService.invalidateAllQueries();
          dispatch(setRelease(res.id));
          notify.info("Release made current");
          return res;
        }
      } catch {
        notify.error("Failed to make release current");
        throw new Error("Failed to make release current");
      }
    },
    [dispatch, mutateAsync, notify],
  );

  return { activateRelease, ...query };
};

export const usePromoteRelease = () => {
  const dispatch = useDispatch();
  const notify = useNotify();
  const { mutateAsync, ...query } = releaseService.useUpdate();

  const promoteRelease = useCallback(
    async (releaseId: string, payload: { description?: string; locale: string; canVector?: boolean }) => {
      try {
        const res = await mutateAsync({
          id: "",
          path: `releases/${releaseId}/custom/deploy`,
          payload,
        });

        if (res?.id) {
          releaseService.invalidateAllQueries();
          dispatch(setRelease(res.id));
          notify.info("Release promoted");
          return res;
        }
      } catch {
        notify.error("Failed to promote release");
        throw new Error("Failed to promote release");
      }
    },
    [dispatch, mutateAsync, notify],
  );

  return { promoteRelease, ...query };
};

/**
 * Gets the current active releases for a project.
 * This will at most be 2 releases - 1 for dev, 1 for prod
 * @param projectId - The project to get releases for, disabled if not provided
 */
export const useCurrentReleases = (projectId: string | undefined) => {
  const {
    data: releases,
    total,
    query,
  } = releaseService.useResources({
    enabled: Boolean(projectId),
    filter: {
      model: projectId,
      activeVersionFlag: true,
      pagination: { perPage: 2, page: 0 },
      fields: ["id", "releaseNo", "env", "description", "workspace"],
    },
  });
  return { releases, total, query };
};

/**
 * Gets a list of releases in release number order.
 * By default, this will return the non active releases.
 * @param projectId - The project to get releases for, disabled if not provided
 * @param activeVersionFlag - If true, will return only active releases. If false, will return only non-active releases. If "all", will return all releases.
 */
type ReleaseVersionFilter = boolean | "all";

export const useRecentReleases = (
  projectId: string | undefined,
  activeVersionFlag: ReleaseVersionFilter = false,
  enabled = true,
) => {
  const {
    data: releases,
    total,
    query,
    loadMore,
  } = releaseService.useInfiniteResources({
    perPage: 10,
    enabled: Boolean(projectId) && enabled,
    filter: {
      model: projectId,
      ...(activeVersionFlag === "all" ? {} : { activeVersionFlag }),
      sort: { field: "releaseNo", order: "desc" },
      fields: ["id", "releaseNo", "env", "description"],
    },
    refetchOnWindowFocus: false,
  });
  return { releases, total, query, loadMore };
};
